import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { AppRoutes } from "@/constants/routes";
import { AppColors } from "@/styles/colors";
import { Flex } from "@chakra-ui/react";
import { CircleCheck, CircleX } from "lucide-react";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";

import { NoData } from "@/components/common/section-components/NoData";
import { CRHeaderElements } from "./schema/header-elements";
import { CashRequestFilter } from "@/components/common/section-components/filters/CashRequestFilter";
import { CashRequestActions } from "@/components/common/section-components/action/CashRequestActions";

import {
  CashRequestEntity,
  cashRequestFilterSchema,
} from "./schema/cash-requests.schema";
import {
  clearFiltersHandler,
  fetchCashRequestsHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { formatCurrency } from "@/utils/format-currency";
import { useRoles } from "@/hooks/useRoles";
import { PermissionCode } from "@/constants/permissions";
import { PendingMessagesAlert } from "@/components/common/section-components/alert/PendingMessagesAlert";
import { CashRequestStatus } from "@/constants/entity-status";
import dayjs from "dayjs";
import { usePolling } from "@/hooks/usePolling";
import { useAuth } from "@/hooks/useAuth";
import { isEmpty } from "@/utils/query-string";

export const CashRequestPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loadingCashRequests, setLoadingCashRequests] =
    useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const [currentFilters, setCurrentFilters] = useState({});

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const { handleSubmit, getValues, setValue, control } = useForm({
    resolver: zodResolver(cashRequestFilterSchema),
  });
  const [page, setPage] = useState(1);

  const { fetchPermissions } = useRoles()

  const { user } = useAuth();

  const clearFilters = async () => {
    setPage(1);
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
      setOpenFilters
    });
  };

  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    await submitFiltersHandler({
      event,
      fetchCashRequests,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      navigate,
      fetchPermissions,
    });
  };

  const navigate = useNavigate();

  const [cashRequests, setCashRequests] = useState<{
    listado: CashRequestEntity[];
    totalPages: number;
    totalRecords: number;
    pendingMessages: boolean;
  }>({
    listado: [],
    totalPages: 1,
    totalRecords: 10,
    pendingMessages: false,
  });
  const [selection, setSelection] = useState<string[]>([]);
  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < selection?.length;
  const handlePageChange = async (page: number) => {
    setPage(page);
    await fetchCashRequests({ ...currentFilters, "P.Pagina": page });
  };

  const handleDetail = (id: any) => {
    navigate(AppRoutes.CASH_REQUEST_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: any) => (
    <CashRequestActions handleDetail={handleDetail} item={item} />
  );

  const fetchCashRequests = async (filters?: any, showLoad = true) => {
    await fetchCashRequestsHandler({
      showLoad,
      setLoadingCashRequests,
      setCashRequests,
      filters,
      user,
      navigate,
      fetchPermissions,
    })
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { state } = useLocation();

  useEffect(() => {
    if (isActive && !isEmpty(user?.localAccountId)) {
      const restoredPage = state?.page;
      if (restoredPage) {
        setPage(restoredPage);
        fetchCashRequests({ ...currentFilters, "P.Pagina": restoredPage });
      } else {
        fetchCashRequests({ ...currentFilters });
      }
    }
  }, [isActive, user]);

  usePolling({
    callback: () =>
      fetchCashRequests({ ...currentFilters, "P.Pagina": page }, false),
    delay: 10000,
    isActive: isActive && !loadingCashRequests,
  });

  const handleAdd = () => {
    navigate(AppRoutes.CASH_REQUEST_CREATION);
  };

  const { userPermissions } = useRoles();

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.CASH_REQUEST &&
        p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        showButtons={hasWriteAccess}
        title="Efectivo"
        loading={loadingCashRequests}
        addButtonLabel="Agregar"
        handleAdd={handleAdd}
      />

      <Flex>
        {cashRequests?.pendingMessages && !loadingCashRequests && (
          <PendingMessagesAlert
            submitFilters={submitFilters}
            setValue={setValue}
          />
        )}
        <CashRequestFilter
          handleSubmit={handleSubmit}
          openFilters={openFilters}
          setOpenFilters={setOpenFilters}
          clearFilters={clearFilters}
          filtersApplied={filtersApplied}
          submitFilters={submitFilters}
          control={control}
          disableFilters={loadingCashRequests}
        />
      </Flex>

      <Flex flexDir="column">
        {Boolean(!loadingCashRequests && !cashRequests?.listado?.length) && (
          <NoData />
        )}
        {Boolean(!loadingCashRequests && cashRequests?.listado?.length) && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={indeterminate}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                cashRequests?.listado?.map((el: CashRequestEntity) => ({
                  ...el,
                  fechaCreacion: dayjs(el?.fechaCreacion).format("DD/MM/YYYY"),
                  monto: `$${formatCurrency(el?.monto)}`,
                  displayEstado: (
                    <RequestStatusBadge
                      status={el?.estadoDescripcion as CashRequestStatus}
                    />
                  ),
                  control: el?.control ? (
                    <Flex justifyContent="center">
                      <CircleCheck color={AppColors.GREEN_ICON} />
                    </Flex>
                  ) : (
                    <Flex justifyContent="center">
                      <CircleX color={AppColors.RED_ICON} />
                    </Flex>
                  ),
                })) ?? []
              }
              headers={CRHeaderElements}
              actionElements={getActionElements}
            />
            {cashRequests?.totalPages > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={cashRequests?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
